import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { Seo } from "@/components/seo/Seo";
import { useI18n } from "@/i18n/I18nProvider";
import { LEGAL_VERSIONS } from "@/lib/legal";

export default function AuthActivate() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();

  const token = useMemo(() => searchParams.get("token") || "", [searchParams]);

  const [loading, setLoading] = useState(true);
  const [previewError, setPreviewError] = useState("");
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [acceptPrivacy, setAcceptPrivacy] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const loadPreview = async () => {
      if (!token) {
        setPreviewError(t("authactivate.missing_token", "Link di attivazione non valido."));
        setLoading(false);
        return;
      }
      try {
        const resp = await fetch("/api/ops?action=guest_preview", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token }),
        });
        const data = await resp.json().catch(() => ({}));
        if (!resp.ok || !data?.ok) {
          throw new Error(data?.error || t("authactivate.invalid_link", "Il link è scaduto o non è valido."));
        }
        setEmail(data.email || "");
        setFullName(data.full_name || "");
      } catch (e: unknown) {
        const message = e instanceof Error ? e.message : t("authactivate.invalid_link", "Il link è scaduto o non è valido.");
        setPreviewError(message);
      } finally {
        setLoading(false);
      }
    };
    loadPreview();
  }, [token, t]);

  const onSubmit = async () => {
    const pwd = (password || "").trim();
    if (pwd.length < 6) {
      toast({
        title: t("authactivate.error_title", "Errore"),
        description: t("authactivate.error_short", "La password deve avere almeno 6 caratteri."),
        variant: "destructive",
      });
      return;
    }
    if (pwd !== (confirmPassword || "").trim()) {
      toast({
        title: t("authactivate.error_title", "Errore"),
        description: t("authactivate.error_mismatch", "Le password non coincidono."),
        variant: "destructive",
      });
      return;
    }
    if (!acceptTerms || !acceptPrivacy) {
      toast({
        title: t("authactivate.legal_required_title", "Consenso richiesto"),
        description: t("authactivate.legal_required_desc", "Accetta Termini e Privacy per attivare il profilo."),
        variant: "destructive",
      });
      return;
    }

    setSaving(true);
    try {
      const resp = await fetch("/api/ops?action=guest_activate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          token,
          password: pwd,
          accept_terms: acceptTerms,
          accept_privacy: acceptPrivacy,
          marketing_consent: marketingConsent,
          legal_versions: LEGAL_VERSIONS,
        }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok || !data?.ok) {
        if (data?.error === "account_exists") {
          toast({
            title: t("authactivate.account_exists_title", "Account già presente"),
            description: t("authactivate.account_exists_desc", "Esiste già un account con questa email. Accedi con la tua password."),
          });
          navigate("/auth");
          return;
        }
        throw new Error(data?.error || t("authactivate.activate_failed", "Attivazione non riuscita."));
      }

      const { error: signInErr } = await supabase.auth.signInWithPassword({
        email: data.email || email,
        password: pwd,
      });
      if (signInErr) {
        toast({
          title: t("authactivate.success_partial_title", "Profilo attivato"),
          description: t("authactivate.success_partial_desc", "Profilo creato. Accedi con la password scelta."),
        });
        navigate("/auth");
        return;
      }

      toast({
        title: t("authactivate.success_title", "Profilo attivato"),
        description: t("authactivate.success_desc", "Benvenuto! Ora puoi gestire le tue prenotazioni."),
      });
      navigate("/dashboard");
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : t("authactivate.activate_failed", "Attivazione non riuscita.");
      toast({
        title: t("authactivate.error_title", "Errore"),
        description: message,
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4 py-8">
      <Seo
        title={t("authactivate.seo_title", "Attiva il tuo profilo | Halomora")}
        noindex
        canonicalPath="/auth/activate"
      />
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>{t("authactivate.title", "Attiva il tuo profilo")}</CardTitle>
          <CardDescription>
            {t(
              "authactivate.subtitle",
              "Abbiamo preparato un profilo con i dati della tua prenotazione. Scegli una password per accedere."
            )}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {loading ? (
            <div className="text-sm text-muted-foreground">
              {t("authactivate.loading", "Verifica del link in corso…")}
            </div>
          ) : previewError ? (
            <div className="space-y-3">
              <div className="text-sm text-red-600 border border-red-200 bg-red-50 rounded p-2">
                {previewError}
              </div>
              <Button variant="outline" onClick={() => navigate("/auth")}>
                {t("authactivate.go_signin", "Vai al login")}
              </Button>
            </div>
          ) : (
            <>
              <div className="space-y-2">
                <Label htmlFor="activate-name">{t("authactivate.full_name", "Nome completo")}</Label>
                <Input id="activate-name" value={fullName} readOnly disabled />
              </div>
              <div className="space-y-2">
                <Label htmlFor="activate-email">{t("authactivate.email", "Email")}</Label>
                <Input id="activate-email" type="email" value={email} readOnly disabled />
              </div>
              <div className="space-y-2">
                <Label htmlFor="activate-password">{t("authactivate.password", "Password")}</Label>
                <Input
                  id="activate-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="activate-confirm">{t("authactivate.confirm_password", "Conferma password")}</Label>
                <Input
                  id="activate-confirm"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  autoComplete="new-password"
                />
              </div>

              <div className="space-y-3 rounded-lg border p-3">
                <div className="flex items-start gap-2">
                  <Checkbox id="activate-terms" checked={acceptTerms} onCheckedChange={(v) => setAcceptTerms(!!v)} />
                  <Label htmlFor="activate-terms" className="text-sm font-normal leading-snug">
                    {t("authactivate.accept_terms", "Accetto i")}{" "}
                    <Link to="/legal/terms" className="underline" target="_blank">
                      {t("authactivate.terms_link", "Termini di servizio")}
                    </Link>
                  </Label>
                </div>
                <div className="flex items-start gap-2">
                  <Checkbox id="activate-privacy" checked={acceptPrivacy} onCheckedChange={(v) => setAcceptPrivacy(!!v)} />
                  <Label htmlFor="activate-privacy" className="text-sm font-normal leading-snug">
                    {t("authactivate.accept_privacy", "Accetto l'")}{" "}
                    <Link to="/legal/privacy" className="underline" target="_blank">
                      {t("authactivate.privacy_link", "Informativa privacy")}
                    </Link>
                  </Label>
                </div>
                <div className="flex items-start gap-2">
                  <Checkbox
                    id="activate-marketing"
                    checked={marketingConsent}
                    onCheckedChange={(v) => setMarketingConsent(!!v)}
                  />
                  <Label htmlFor="activate-marketing" className="text-sm font-normal leading-snug text-muted-foreground">
                    {t(
                      "authactivate.marketing_opt_in",
                      "Voglio ricevere novità e offerte utili (facoltativo)."
                    )}
                  </Label>
                </div>
              </div>

              <Button className="w-full" onClick={onSubmit} disabled={saving}>
                {saving ? t("authactivate.saving", "Attivazione…") : t("authactivate.cta", "Attiva profilo")}
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
